/**
 * Mock Role Provider
 * 
 * Implements RoleProvider using localStorage for persistence.
 * Part of PE-894: Create roleService.ts with Gobodo CRUD
 */

import { createPersistedMockData } from '../mockDataManager';
import {
  Role,
  RoleProvider,
  RoleFilters,
  RoleHierarchyNode,
  CreateRoleRequest,
  UpdateRoleRequest,
  ObjectPermission,
  SearchPermissions
} from './roleProvider';

// ─── Shared helpers for default timestamps ──────────────────────────────────
const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();

// All object permission entries used across roles
const OBJ = {
  client:       (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_client',        objectName: 'Client',          read: r, write: w, delete: d }),
  case:         (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_case',          objectName: 'Case',            read: r, write: w, delete: d }),
  task:         (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_task',          objectName: 'Task',            read: r, write: w, delete: d }),
  user:         (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_user',          objectName: 'User',            read: r, write: w, delete: d }),
  csClient:     (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_cs_client',    objectName: 'CyberSweepClient', read: r, write: w, delete: d }),
  csAudit:      (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_cs_audit',     objectName: 'CSAudit',         read: r, write: w, delete: d }),
  pawRecord:    (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_paw_record',   objectName: 'PAWRecord',       read: r, write: w, delete: d }),
  creditBlock:  (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_credit_block', objectName: 'CreditBlock',     read: r, write: w, delete: d }),
  bureaus:      (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_bureaus',      objectName: 'Credit_Bureaus',  read: r, write: w, delete: d }),
  unlockReq:    (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_unlock_req',   objectName: 'Unlock_Request',  read: r, write: w, delete: d }),
  contact:      (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_contact',      objectName: 'Contact',         read: r, write: w, delete: d }),
  lead:         (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_lead',         objectName: 'Lead',            read: r, write: w, delete: d }),
  opportunity:  (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_opportunity',  objectName: 'Opportunity',     read: r, write: w, delete: d }),
  event:        (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_event',        objectName: 'Event',           read: r, write: w, delete: d }),
  report:       (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_report',       objectName: 'Report',          read: r, write: w, delete: d }),
  account:      (r: boolean, w: boolean, d: boolean) => ({ objectId: 'obj_account',      objectName: 'Account',         read: r, write: w, delete: d }),
};

const ALL_VISIBLE_CATEGORIES: SearchPermissions['visibleCategories'] = [
  'objects', 'workflows', 'fields', 'settings', 'reports', 'dashboards',
  'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms',
];

// ─── Operational roles (per Dr. Cris onboarding list, April 2026) ───────────
// See docs/RBAC_OPERATIONALIZATION.md for the full permission matrices.
const defaultMockRoles: Role[] = [
  // 1. System Administrator
  {
    id: 'role_admin',
    name: 'System Administrator',
    description: 'Full system access with all permissions',
    isSystem: true,
    permissions: [
      OBJ.client(true, true, true), OBJ.case(true, true, true), OBJ.task(true, true, true),
      OBJ.user(true, true, true), OBJ.csClient(true, true, true), OBJ.csAudit(true, true, true),
      OBJ.pawRecord(true, true, true), OBJ.creditBlock(true, true, true), OBJ.bureaus(true, true, true),
      OBJ.unlockReq(true, true, true), OBJ.contact(true, true, true), OBJ.lead(true, true, true),
      OBJ.opportunity(true, true, true), OBJ.event(true, true, true), OBJ.report(true, true, true),
      OBJ.account(true, true, true),
    ],
    userCount: 2,
    createdDate: daysAgo(365), createdBy: 'System', lastModified: daysAgo(365),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: true, canSearchWorkflows: true,
      canSearchSystemSettings: true, canSearchUsers: true, canSearchRoles: true,
      canSearchSecurity: true, canSearchReports: true, canSearchDashboards: true,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: true, canSearchOpportunities: true, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: true, canSearchCSAudits: true,
      canSearchPAWRecords: true, canSearchCreditCases: true, canSearchTradelines: true,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ALL_VISIBLE_CATEGORIES,
    },
  },

  // 2. Operations Manager
  {
    id: 'role_ops_manager',
    name: 'Operations Manager',
    description: 'Cross-app operations oversight — all apps, no system admin',
    parentRoleId: 'role_admin', parentRoleName: 'System Administrator',
    isSystem: false,
    permissions: [
      OBJ.client(true, true, false), OBJ.case(true, true, true), OBJ.task(true, true, true),
      OBJ.user(true, false, false), OBJ.csClient(true, true, false), OBJ.csAudit(true, true, false),
      OBJ.pawRecord(true, true, false), OBJ.creditBlock(true, true, false), OBJ.bureaus(true, true, false),
      OBJ.unlockReq(true, true, false), OBJ.contact(true, true, false), OBJ.lead(true, true, false),
      OBJ.opportunity(true, true, false), OBJ.event(true, true, false), OBJ.report(true, true, false),
      OBJ.account(true, true, false),
    ],
    userCount: 1,
    createdDate: daysAgo(300), createdBy: 'Admin', lastModified: daysAgo(30),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: true, canSearchWorkflows: true,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: true, canSearchDashboards: true,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: true, canSearchOpportunities: true, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: true, canSearchCSAudits: true,
      canSearchPAWRecords: true, canSearchCreditCases: true, canSearchTradelines: true,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'workflows', 'fields', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms'],
    },
  },

  // 3. Credit Block Team Lead
  {
    id: 'role_cb_lead',
    name: 'Credit Block Team Lead',
    description: 'Full Credit Block access — manages CB team and cases',
    parentRoleId: 'role_ops_manager', parentRoleName: 'Operations Manager',
    isSystem: false,
    permissions: [
      OBJ.client(true, true, false), OBJ.case(true, true, false), OBJ.task(true, true, false),
      OBJ.user(true, false, false), OBJ.creditBlock(true, true, false), OBJ.bureaus(true, true, false),
      OBJ.unlockReq(true, true, false), OBJ.contact(true, false, false), OBJ.event(true, false, false),
      OBJ.report(true, false, false), OBJ.account(true, false, false),
    ],
    userCount: 1,
    createdDate: daysAgo(60), createdBy: 'Admin', lastModified: daysAgo(7),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: false, canSearchWorkflows: false,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: true, canSearchDashboards: true,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: false, canSearchOpportunities: false, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: false, canSearchCSAudits: false,
      canSearchPAWRecords: false, canSearchCreditCases: false, canSearchTradelines: false,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'cases', 'firms'],
    },
  },

  // 4. Credit Block Processor
  {
    id: 'role_cb_processor',
    name: 'Credit Block Processor',
    description: 'Day-to-day Credit Block case processing',
    parentRoleId: 'role_cb_lead', parentRoleName: 'Credit Block Team Lead',
    isSystem: false,
    permissions: [
      OBJ.client(true, false, false), OBJ.case(true, true, false), OBJ.task(true, true, false),
      OBJ.user(true, false, false), OBJ.creditBlock(true, true, false), OBJ.bureaus(true, false, false),
      OBJ.unlockReq(true, true, false), OBJ.contact(true, false, false), OBJ.event(true, false, false),
      OBJ.report(true, false, false), OBJ.account(true, false, false),
    ],
    userCount: 4,
    createdDate: daysAgo(60), createdBy: 'Admin', lastModified: daysAgo(7),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: false, canSearchWorkflows: false,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: false, canSearchDashboards: false,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: false, canSearchOpportunities: false, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: false, canSearchCSAudits: false,
      canSearchPAWRecords: false, canSearchCreditCases: false, canSearchTradelines: false,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'profile', 'clients', 'contacts', 'cases', 'firms'],
    },
  },

  // 5. CyberSweep Team Lead
  {
    id: 'role_cs_lead',
    name: 'CyberSweep Team Lead',
    description: 'Full CyberSweep access — manages CS team and audits',
    parentRoleId: 'role_ops_manager', parentRoleName: 'Operations Manager',
    isSystem: false,
    permissions: [
      OBJ.client(true, true, false), OBJ.case(true, true, false), OBJ.task(true, true, false),
      OBJ.user(true, false, false), OBJ.csClient(true, true, false), OBJ.csAudit(true, true, false),
      OBJ.pawRecord(true, true, false), OBJ.contact(true, false, false), OBJ.event(true, false, false),
      OBJ.report(true, false, false), OBJ.account(true, false, false),
    ],
    userCount: 1,
    createdDate: daysAgo(60), createdBy: 'Admin', lastModified: daysAgo(7),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: false, canSearchWorkflows: false,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: true, canSearchDashboards: true,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: false, canSearchOpportunities: false, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: true, canSearchCSAudits: true,
      canSearchPAWRecords: true, canSearchCreditCases: false, canSearchTradelines: false,
      canSearchCreditBlockAccounts: false,
      visibleCategories: ['objects', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'cases', 'firms'],
    },
  },

  // 6. CyberSweep Auditor
  {
    id: 'role_cs_auditor',
    name: 'CyberSweep Auditor',
    description: 'Executes CyberSweep audits — read clients, read/write audits',
    parentRoleId: 'role_cs_lead', parentRoleName: 'CyberSweep Team Lead',
    isSystem: false,
    permissions: [
      OBJ.client(true, false, false), OBJ.case(true, false, false), OBJ.task(true, false, false),
      OBJ.csClient(true, false, false), OBJ.csAudit(true, true, false),
      OBJ.pawRecord(true, true, false), OBJ.event(true, false, false),
      OBJ.report(true, false, false),
    ],
    userCount: 3,
    createdDate: daysAgo(60), createdBy: 'Admin', lastModified: daysAgo(7),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: false, canSearchWorkflows: false,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: false, canSearchDashboards: false,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: false,
      canSearchLeads: false, canSearchOpportunities: false, canSearchCases: true,
      canSearchFirms: false, canSearchCyberSweepClients: true, canSearchCSAudits: true,
      canSearchPAWRecords: true, canSearchCreditCases: false, canSearchTradelines: false,
      canSearchCreditBlockAccounts: false,
      visibleCategories: ['objects', 'profile', 'clients', 'cases'],
    },
  },

  // 7. Data Analyst
  {
    id: 'role_data_analyst',
    name: 'Data Analyst',
    description: 'Cross-app read-only access with reports and dashboards',
    parentRoleId: 'role_admin', parentRoleName: 'System Administrator',
    isSystem: false,
    permissions: [
      OBJ.client(true, false, false), OBJ.case(true, false, false), OBJ.task(true, false, false),
      OBJ.user(true, false, false), OBJ.csClient(true, false, false), OBJ.csAudit(true, false, false),
      OBJ.pawRecord(true, false, false), OBJ.creditBlock(true, false, false), OBJ.bureaus(true, false, false),
      OBJ.unlockReq(true, false, false), OBJ.contact(true, false, false), OBJ.lead(true, false, false),
      OBJ.opportunity(true, false, false), OBJ.event(true, false, false), OBJ.report(true, false, false),
      OBJ.account(true, false, false),
    ],
    userCount: 1,
    createdDate: daysAgo(30), createdBy: 'Admin', lastModified: daysAgo(7),
    searchPermissions: {
      canSearchObjects: true, canSearchFields: false, canSearchWorkflows: false,
      canSearchSystemSettings: false, canSearchUsers: false, canSearchRoles: false,
      canSearchSecurity: false, canSearchReports: true, canSearchDashboards: true,
      canSearchProfileSettings: true, canSearchClients: true, canSearchContacts: true,
      canSearchLeads: true, canSearchOpportunities: true, canSearchCases: true,
      canSearchFirms: true, canSearchCyberSweepClients: true, canSearchCSAudits: true,
      canSearchPAWRecords: true, canSearchCreditCases: false, canSearchTradelines: false,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms'],
    },
  },
];

// Persisted mock data storage
const persistedRoles = createPersistedMockData('crm_roles_v2', defaultMockRoles);

/**
 * Build role hierarchy tree from flat list
 */
function buildRoleHierarchy(roles: Role[]): RoleHierarchyNode[] {
  const roleMap = new Map<string, RoleHierarchyNode>();
  const rootRoles: RoleHierarchyNode[] = [];

  // Initialize all roles with empty children
  roles.forEach(role => {
    roleMap.set(role.id, { ...role, children: [] });
  });

  // Build hierarchy
  roles.forEach(role => {
    const node = roleMap.get(role.id)!;
    if (role.parentRoleId) {
      const parent = roleMap.get(role.parentRoleId);
      if (parent) {
        parent.children.push(node);
      } else {
        rootRoles.push(node);
      }
    } else {
      rootRoles.push(node);
    }
  });

  return rootRoles;
}

/**
 * Mock Role Provider Implementation
 * 
 * Uses localStorage for data persistence via mockDataManager.
 */
export class MockRoleProvider implements RoleProvider {
  private simulateDelay: boolean;

  constructor(options?: { simulateDelay?: boolean }) {
    this.simulateDelay = options?.simulateDelay ?? true;
  }

  private async delay(ms: number = 300): Promise<void> {
    if (this.simulateDelay) {
      await new Promise(resolve => setTimeout(resolve, ms));
    }
  }

  async getRoles(filters?: RoleFilters): Promise<Role[]> {
    await this.delay(400);

    let roles = persistedRoles.get();

    // Apply search filter
    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      roles = roles.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }

    // Filter out system roles if requested
    if (filters?.includeSystem === false) {
      roles = roles.filter(r => !r.isSystem);
    }

    // Filter by parent role
    if (filters?.parentRoleId) {
      roles = roles.filter(r => r.parentRoleId === filters.parentRoleId);
    }

    // Sort by name
    return roles.sort((a, b) => a.name.localeCompare(b.name));
  }

  async getRoleById(id: string): Promise<Role | null> {
    await this.delay(300);
    return persistedRoles.get().find(r => r.id === id) || null;
  }

  async createRole(request: CreateRoleRequest): Promise<Role> {
    await this.delay(600);

    // Lookup parent role name if parentRoleId provided
    let parentRoleName: string | undefined;
    if (request.parentRoleId) {
      const parent = persistedRoles.get().find(r => r.id === request.parentRoleId);
      parentRoleName = parent?.name;
    }

    const newRole: Role = {
      id: `role_${Date.now()}`,
      name: request.name,
      description: request.description,
      parentRoleId: request.parentRoleId,
      parentRoleName,
      isSystem: false,
      permissions: request.permissions || [],
      searchPermissions: request.searchPermissions,
      userCount: 0,
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    return persistedRoles.add(newRole);
  }

  async updateRole(id: string, updates: UpdateRoleRequest): Promise<Role> {
    await this.delay(500);

    const role = persistedRoles.get().find(r => r.id === id);
    if (!role) {
      throw new Error('Role not found');
    }

    // For system roles, only allow updating permissions and searchPermissions
    let finalUpdates: Partial<Role> = {};
    if (role.isSystem) {
      if (updates.permissions) finalUpdates.permissions = updates.permissions;
      if (updates.searchPermissions) finalUpdates.searchPermissions = updates.searchPermissions;
    } else {
      // Non-system roles can update everything
      if (updates.name !== undefined) finalUpdates.name = updates.name;
      if (updates.description !== undefined) finalUpdates.description = updates.description;
      if (updates.parentRoleId !== undefined) {
        finalUpdates.parentRoleId = updates.parentRoleId;
        // Lookup parent name
        if (updates.parentRoleId) {
          const parent = persistedRoles.get().find(r => r.id === updates.parentRoleId);
          finalUpdates.parentRoleName = parent?.name;
        } else {
          finalUpdates.parentRoleName = undefined;
        }
      }
      if (updates.permissions !== undefined) finalUpdates.permissions = updates.permissions;
      if (updates.searchPermissions !== undefined) finalUpdates.searchPermissions = updates.searchPermissions;
    }

    finalUpdates.lastModified = new Date().toISOString();

    const updatedRole = persistedRoles.update(id, finalUpdates);
    if (!updatedRole) {
      throw new Error('Failed to update role');
    }

    return updatedRole;
  }

  async deleteRole(id: string): Promise<void> {
    await this.delay(400);

    const role = persistedRoles.get().find(r => r.id === id);
    if (!role) {
      throw new Error('Role not found');
    }

    if (role.isSystem) {
      throw new Error('Cannot delete system roles');
    }

    if (role.userCount > 0) {
      throw new Error(`Cannot delete role with ${role.userCount} assigned user${role.userCount > 1 ? 's' : ''}`);
    }

    const deleted = persistedRoles.remove(id);
    if (!deleted) {
      throw new Error('Failed to delete role');
    }
  }

  async cloneRole(id: string, newName: string): Promise<Role> {
    await this.delay(500);

    const role = persistedRoles.get().find(r => r.id === id);
    if (!role) {
      throw new Error('Role not found');
    }

    const clonedRole: Role = {
      ...role,
      id: `role_${Date.now()}`,
      name: newName || `${role.name} (Copy)`,
      isSystem: false,
      userCount: 0,
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    return persistedRoles.add(clonedRole);
  }

  async getRoleHierarchy(): Promise<RoleHierarchyNode[]> {
    await this.delay(400);
    return buildRoleHierarchy(persistedRoles.get());
  }

  /**
   * Reset roles to defaults (for testing)
   */
  reset(): void {
    persistedRoles.reset();
  }
}

// Export singleton instance
export const mockRoleProvider = new MockRoleProvider();

// Export for testing with no delay
export function createMockRoleProvider(options?: { simulateDelay?: boolean }): MockRoleProvider {
  return new MockRoleProvider(options);
}
